import React, { useEffect, useState } from "react";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { IconCalendar, IconFilter, IconFilterOff } from "@tabler/icons-react";
import CreateKomisi from "./components/create-komisi";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import { useKomisiBooking } from "contexts/KomisiBookingContext";
import { AgentProvider, useAgent } from "contexts/v1/masterData/AgentContext";
import Swal from "sweetalert2";
import PrintSusuk from "./components/PrintSusuk";
import Select from "react-select";
import DateRangePicker from "./components/DateRangePicker";
import ExportExcelSusuk from "./components/ExportExcelSusuk";
import ChangeSusukStatusButton from "./components/ChangeSusukStatusButton";
import printJS from "print-js";
import PermissionMiddleware from "middleware/PermissionMiddleware";

const KomisiAgent = () => {
  const { handleSuccess, showCanvasHandler } = useCanvasHandler();
  const {
    sessions,
    dataPerTrip,
    bookings,
    filterOptions,
    updateFilter,
    refetch,
  } = useKomisiBooking();
  const { agents } = useAgent();
  const [showFilters, setShowFilters] = useState(true); // State untuk show/hide filter

  // 🔹 Convert agent list to select format
  const agentOptions =
    agents?.data?.map((agent) => ({
      value: agent.id,
      label: agent.name,
    })) || [];

  // 🔹 Toggle Paid/Unpaid Susuk
  const [showPaidSusuk, setShowPaidSusuk] = useState(
    filterOptions.filters.susuk_agent === "not_empty"
  );
  const togglePaidSusuk = () => {
    setShowPaidSusuk((prev) => !prev);
    updateFilter("susuk_agent", showPaidSusuk ? "empty" : "not_empty");
  };

  // 🔹 Date Range Handling
  const [dateRange, setDateRange] = useState({
    startDate: filterOptions.filters.reservation_date.gte,
    endDate: filterOptions.filters.reservation_date.lte,
  });

  useEffect(() => {
    updateFilter("reservation_date", {
      gte: dateRange.startDate,
      lte: dateRange.endDate,
    });
  }, [dateRange]);

  // 🔹 Handle Update Susuk
  const handleUpdateVold = (e, booking) => {
    console.log("handleUpdate booking", booking);
    e.preventDefault();
    const isPaid = !!booking.susuk_paid_at;

    if (isPaid) {
      Swal.fire({
        title: "Enter Admin Password",
        input: "password",
        inputPlaceholder: "Enter password",
        showCancelButton: true,
        confirmButtonText: "Submit",
        preConfirm: (password) => {
          if (password === process.env.REACT_APP_ADMIN_PASSWORD) return true;
          Swal.showValidationMessage("Incorrect password");
          return false;
        },
      }).then((result) => {
        if (result.isConfirmed) showKomisiForm(booking);
      });
    } else {
      showKomisiForm(booking);
    }
  };

  const handleUpdate = async (e, booking) => {
    console.log("handleUpdate booking", booking);
    e.preventDefault();
    const isPaid = !!booking.susuk_agent;

    if (isPaid) {
      try {
        const result = await Swal.fire({
          title: "Enter Admin Password",
          input: "password",
          inputPlaceholder: "Enter password",
          showCancelButton: true,
          confirmButtonText: "Submit",
          preConfirm: (password) => {
            if (password === process.env.REACT_APP_ADMIN_PASSWORD) return true;
            Swal.showValidationMessage("Incorrect password");
            return false;
          },
        });

        if (result.isConfirmed) {
          showKomisiForm(booking);
        }
      } catch (err) {
        console.error("Error during admin password prompt", err);
      }
    } else {
      showKomisiForm(booking);
    }
  };

  const showKomisiForm = (booking) => {
    showCanvasHandler(
      "Susuk Agent",
      <AgentProvider>
        <CreateKomisi
          booking={booking}
          // onSuccess={handleSuccess}
          onSuccess={() => {
            Swal.fire("Success", "Susuk has been updated", "success");
            refetch();
          }}
        />
      </AgentProvider>
    );
  };

  useEffect(() => {
    if (filterOptions.filters.agent_id !== "") {
      refetch();
    }
  }, [filterOptions.filters.agent_id]);

  return (
    <LayoutDashboard>
      <div className="container-fluid mt-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          {/* <h5>Filters</h5> */}
          <h1>Susuk Agent Report</h1>

          <button
            className="btn btn-outline-primary"
            onClick={() => setShowFilters(!showFilters)}
          >
            {/* {showFilters ? "Hide Filters" : "Show Filters"} */}
            {showFilters ? (
              <IconFilterOff size={24} />
            ) : (
              <IconFilter size={24} />
            )}
          </button>
        </div>
        {showFilters && (
          <>
            {/* 🔹 Filters */}
            <div className="row mb-3">
              <div className="col-md-3">
                <SelectAgent agentOptions={agentOptions} />
              </div>
              <div className="col-md-4">
                <SearchInput />
              </div>
              <div className="col text-end">
                <DateRangePicker value={dateRange} onChange={setDateRange} />
              </div>
            </div>
            <div className="row mb-3 align-items-center justify-content-between">
              {/* 🔹 Status Filter */}
              <div className="col-md-auto d-flex align-items-center h-100 py-2">
                <strong>Data loaded for:</strong>
                <span className="mx-1">
                  {filterOptions.filters.reservation_date?.gte} -{" "}
                  {filterOptions.filters.reservation_date?.lte}
                </span>
              </div>
              <div className="col-md-auto d-flex align-items-center h-100">
                <ExportExcelSusuk
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                />
              </div>
              {/* 🔹 Toggle Switch */}
              <div className="col-md-auto d-flex align-items-center h-100">
                <div className="form-check form-switch py-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={showPaidSusuk}
                    onChange={togglePaidSusuk}
                    style={{ cursor: "pointer" }}
                  />
                  <label className="form-check-label fw-bold">
                    Show Susuk:
                    <span
                      className={`ms-2 ${
                        showPaidSusuk ? "text-success" : "text-danger"
                      }`}
                    >
                      {showPaidSusuk
                        ? "SUDAH DIHITUNG ✅"
                        : "BELUM DIHITUNG ❌"}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 🔹 Booking Table */}
        <div className="table-responsive">
          <table className="table table-bordered table-hover">
            {/* <thead>
              <tr>
                <th>#</th>
              </tr>
            </thead> */}
            <tbody>
              {sessions?.map((session) => (
                <SessionBlock
                  key={session.id}
                  session={session}
                  dataPerTrip={dataPerTrip}
                  refetch={refetch}
                  handleUpdate={handleUpdate}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </LayoutDashboard>
  );
};

export default KomisiAgent;

/* 🔹 SESSION BLOCK */
const SessionBlock = ({ session, dataPerTrip, refetch, handleUpdate }) => {
  const bookingsForSession = dataPerTrip[session.id] || [];
  return (
    <div className="col-12 mb-3">
      <div className="shadow-sm card p-0 border-1">
        <div className="card-header p-3">
          <h2 className="m-0">{session.title}</h2>
        </div>
        <div className="card-body">
          <BookingTable
            bookings={bookingsForSession}
            refetch={refetch}
            handleUpdate={handleUpdate}
          />
        </div>
      </div>
    </div>
  );
};

/* 🔹 SEARCH INPUT */
const SearchInput = () => {
  const { handleSearch, filterOptions } = useKomisiBooking();
  const [search, setSearch] = useState(filterOptions.search || "");

  return (
    <>
      <label className="form-label">Search</label>
      <input
        type="text"
        className="form-control"
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSearch(e)}
      />
    </>
  );
};

/* 🔹 BOOKING TABLE */
const BookingTable = ({ bookings, handleUpdate, refetch }) => {
  // console.log("BookingTable bookings", bookings);
  // Fungsi getTextColor
  const getTextColor = (backgroundColor) => {
    const hex = backgroundColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance < 128 ? "#fff" : "#000";
  };

  const [selectedBookings, setSelectedBookings] = useState([]);

  // 🔹 Fungsi untuk menangani perubahan checkbox
  const handleCheckboxChange = (bookingId) => {
    setSelectedBookings(
      (prevSelected) =>
        prevSelected.includes(bookingId)
          ? prevSelected.filter((id) => id !== bookingId) // Hapus jika sudah ada
          : [...prevSelected, bookingId] // Tambah jika belum ada
    );
  };

  // 🔹 Fungsi untuk memilih semua checkbox yang memenuhi syarat (susuk_agent sudah dihitung)
  const handleSelectAll = (e) => {
    setSelectedBookings(
      e.target.checked
        ? bookings
            .filter((b) => b.susuk_agent) // ✅ Hanya booking dengan `susuk_agent`
            .map((b) => b.id)
        : []
    );
  };

  // 🔹 Fungsi untuk mencetak booking yang dipilih
  const handlePrintSelected = () => {
    if (selectedBookings.length === 0) {
      Swal.fire(
        "Error",
        "Pilih setidaknya satu booking untuk dicetak!",
        "error"
      );
      return;
    }

    // 🔹 Ambil semua elemen yang tersedia untuk dicetak
    const printableElements = selectedBookings
      .map((id) => {
        const element = document.getElementById(`print-content-${id}`);
        return element ? element.innerHTML : null;
      })
      .filter((html) => html !== null) // Hanya elemen valid
      .join("<br/><br/>");

    if (!printableElements) {
      Swal.fire("Error", "Tidak ada struk yang dapat dicetak!", "error");
      return;
    }

    Swal.fire({
      title: "Konfirmasi Cetak",
      text: `Anda akan mencetak ${selectedBookings.length} struk. Lanjutkan?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cetak",
      cancelButtonText: "Batal",
    }).then((result) => {
      if (result.isConfirmed) {
        printJS({
          printable: printableElements,
          type: "raw-html",
          targetStyles: ["*"],
        });

        Swal.fire({
          title: "Sedang Mencetak",
          text: "Struk sedang diproses...",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        // Reset daftar yang dipilih setelah cetak
        setSelectedBookings([]);
        refetch();
      }
    });
  };

  return (
    <>
      {/* 🔹 Tombol Print All hanya muncul jika ada booking dengan `susuk_agent` */}
      {bookings.some((b) => b.susuk_agent) && (
        <div className="d-flex mb-3">
          <button
            className="btn btn-primary"
            onClick={handlePrintSelected}
            disabled={selectedBookings.length === 0}
          >
            Print Selected ({selectedBookings.length})
          </button>
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-hover table-striped table-bordered">
          <thead>
            <tr>
              {/* 🔹 Checkbox Select All hanya muncul jika ada booking yang bisa dipilih */}
              {bookings.some((b) => b.susuk_agent) && (
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={
                      selectedBookings.length > 0 &&
                      selectedBookings.length ===
                        bookings.filter((b) => b.susuk_agent).length
                    }
                  />
                </th>
              )}
              <th>Code #</th>
              <th>Action</th>
              <th>Date</th>
              <th>PAX</th>
              <th>Driver</th>
              <th>Agent</th>
              <th>Label</th>
              <th>Transport</th>
              <th>TRACK</th>
              <th>Guest</th>
              <th>Hotel</th>
              <th>Total Sales</th>
              <th>Susuk</th>
              <th>Note Susuk</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {bookings?.map((booking, index) => (
              <tr key={index}>
                {/* 🔹 Checkbox hanya untuk booking dengan `susuk_agent` */}
                {booking.susuk_agent && (
                  <td>
                    <input
                      type="checkbox"
                      onChange={() => handleCheckboxChange(booking.id)}
                      checked={selectedBookings.includes(booking.id)}
                    />
                  </td>
                )}
                <td>{booking.code}</td>
                <td className="d-flex align-items-center">
                  <button
                    className="btn btn-primary me-2"
                    onClick={(e) => handleUpdate(e, booking)}
                  >
                    Susuk
                  </button>
                  {/* print jon booking */}
                  {/* <PrintSusuk
                booking={booking}
                onSuccess={() => {
                  Swal.fire("Print Success", "", "success");
                  refetch();
                }}
              /> */}
                  {booking.susuk_agent && (
                    <PrintSusuk
                      booking={booking}
                      onSuccess={() => {
                        Swal.fire("Print Success", "", "success");
                        refetch();
                      }}
                    />
                  )}
                  {/* change status utnuk paid dan unpaid */}
                  {/* {booking.susuk_agent && (
                <button
                  className={`btn btn-sm ms-2 text-uppercase ${
                    booking.susuk_paid_at ? "btn-success" : "btn-danger"
                  }`}
                  onClick={() => {
                    Swal.fire({
                      title: "Are you sure?",
                      text: `Change status to ${
                        booking.susuk_paid_at ? "unpaid" : "paid"
                      }`,
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonText: "Yes",
                      cancelButtonText: "No",
                    }).then((result) => {
                      if (result.isConfirmed) {
                        refetch();
                      }
                    });
                  }}
                >
                  {booking.status_susuk_agent}
                </button>
              )}
              V2:  */}
                  {booking.susuk_agent && (
                    <ChangeSusukStatusButton
                      booking={booking}
                      refetch={refetch}
                    />
                  )}
                </td>
                <td>{booking.reservation_date}</td>
                <td>{booking.guest_pax}</td>
                <td>{booking.driver}</td>

                {/* <td><GetAgentName booking={booking} /></td> */}
                <td>
                  <span
                    className="badge text-uppercase"
                    style={{
                      backgroundColor: booking.agent?.color || "#6c757d",
                      color: getTextColor(booking.agent?.color || "#6c757d"), // Warna teks dinamis
                    }}
                  >
                    {booking.agent?.name || "No Agent"}
                  </span>
                </td>
                <td>
                  {booking.labels?.length > 0 ? (
                    booking.labels.map((label, index) => (
                      <span
                        key={index}
                        className="badge text-uppercase me-1"
                        style={{
                          backgroundColor: label.color || "#6c757d",
                          color: getTextColor(label.color || "#6c757d"), // Warna teks dinamis
                        }}
                      >
                        {label.name || "No Label"}
                      </span>
                    ))
                  ) : (
                    <span className="badge text-uppercase">No Label</span>
                  )}
                </td>
                {/* print json booking */}
                {/* <td>
                <pre>{JSON.stringify(booking, null, 2)}</pre>
              </td> */}
                {/* <td>{booking.guest_transport || "-"}</td> */}
                <td>
                  {booking.guest_transport === "PU" ? (
                    <span className="badge bg-success text-white">
                      {booking.guest_transport}
                    </span>
                  ) : (
                    <span className="badge text-uppercase">
                      {booking.guest_transport || "-"}
                    </span>
                  )}
                </td>
                <td>
                  {booking.track === "A" ? (
                    <span className="badge">
                      {booking.track} <br />
                      <small>PREMIUM</small>
                    </span>
                  ) : (
                    <span className="badge bg-red text-white">
                      {booking.track} <br />
                      <small>REGULAR</small>
                    </span>
                  )}
                </td>
                <td>{booking.guest_name}</td>
                <td>{booking.guest_nationality}</td>
                <td>Rp.{formatPrice(booking.total_price)}</td>
                <td>Rp.{formatPrice(booking.susuk_agent)}</td>
                <td>
                  {booking.note_susuk ? (
                    <span className="badge bg-info text-white">
                      {booking.note_susuk}
                    </span>
                  ) : (
                    <span className="badge bg-secondary text-white">
                      No Note
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <PermissionMiddleware allowedPermissions={["see-susuk-agent"]}>
            <tfoot>
              <tr>
                <td colSpan={14} className="text-end fw-bold">
                  Total Sales: Rp.
                  {formatPrice(
                    bookings.reduce(
                      (sum, b) => sum + (parseFloat(b.total_price) || 0),
                      0
                    )
                  )}
                  &nbsp;|&nbsp; Total Susuk: Rp.
                  {formatPrice(
                    bookings.reduce(
                      (sum, b) => sum + (parseFloat(b.susuk_agent) || 0),
                      0
                    )
                  )}
                </td>
              </tr>
            </tfoot>
          </PermissionMiddleware>
        </table>
      </div>
    </>
  );
};

/* 🔹 FORMAT PRICE */
const formatPrice = (price) => new Intl.NumberFormat("id-ID").format(price);

/* 🔹 GET AGENT NAME */
const GetAgentName = ({ booking }) => {
  const { agents } = useAgent();
  const agent = agents.data.find((a) => a.id === booking.agent_id) || {};
  return (
    <span
      className="badge text-uppercase"
      style={{ backgroundColor: agent.color || "#6c757d" }}
    >
      {agent.name || "Unknown"}
    </span>
  );
};

/* 🔹 SELECT AGENT */
const SelectAgent = ({ agentOptions }) => {
  const { filterOptions, updateFilter } = useKomisiBooking();

  return (
    <>
      <label className="form-label">Agent</label>
      <Select
        options={agentOptions}
        value={
          agentOptions.find(
            (a) => a.value === filterOptions.filters.agent_id
          ) || null
        }
        onChange={(selectedOption) => {
          updateFilter("agent_id", selectedOption ? selectedOption.value : ""); // ✅ Jika null, set kosong
        }}
        isClearable
        placeholder="Select Agent"
      />
    </>
  );
};
